import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { CheckoutStateProps, useCheckoutContext } from '@zooplus/checkout-state';
import { findDeploymentEnvFromUrl } from '@zooplus/commons-lang';
import { Alert } from '@zooplus/ui-components';

import { useTranslation } from '@hooks/use-translations';
import { CHECKOUT_STATE_PROJECT_ID, SEPA_EXPRESS_TIMEOUT } from '@utilities/constants';

import { ErrorCode, SepaRejectResponseType, SepaSuccessResponseType } from './sepa-express-types';
import {
  addStyleToPlaceOrderButton,
  getSepaExpressLibraryUrl,
  removeButtonContainer,
  trackPaymentEvent,
  trackSepaExpressButtonClick,
} from './sepa-express-utils';
import styles from './sepa-express.module.scss';

const INTERVAL_TIMEOUT = 100;

const SepaExpressButton = () => {
  const { checkoutState = {} as CheckoutStateProps } =
    useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { t } = useTranslation();
  const [errorType, setErrorType] = useState(ErrorCode.NO_ERROR);

  const sepaExpressExternalScript = useMemo(() => {
    return getSepaExpressLibraryUrl(findDeploymentEnvFromUrl());
  }, []);

  const setupPopupMiddleware = useCallback(() => {
    const originalWindowOpenFn = window.open;
    window.open = (...args) => {
      trackPaymentEvent({ type: 'Bank_popup_impression' });
      const sepaPopUpWindow = originalWindowOpenFn(...args);

      const sepaPopupIntervalId = setInterval(() => {
        if (sepaPopUpWindow?.closed) {
          trackPaymentEvent({ type: 'Bank_popup_close' });
          clearInterval(sepaPopupIntervalId);
          window.open = originalWindowOpenFn;
        }
      }, INTERVAL_TIMEOUT);
      return sepaPopUpWindow;
    };
  }, []);

  const handleInitialState = useCallback(() => {
    const {
      payment: { sepaExpressButton: { hostedPageId } = {} },
    } = checkoutState;
    window.sepaExpress.init(hostedPageId);
  }, [checkoutState]);

  const handleReadyState = useCallback(() => {
    let isSepaExpressReady = false;
    const sepaExpressTimeoutId = setTimeout(() => {
      if (!isSepaExpressReady) {
        setErrorType(ErrorCode.TIMEOUT);
        trackPaymentEvent({ type: 'SEPA_buy_button_timeout' });
        removeButtonContainer();
      }
    }, SEPA_EXPRESS_TIMEOUT);

    window.sepaExpress.ready(() => {
      setupPopupMiddleware();
      isSepaExpressReady = true;

      const stylingInterval = setInterval(() => {
        const sepaButtonElement = document.getElementById('sepaExpressButton') as HTMLButtonElement;
        if (sepaButtonElement) {
          addStyleToPlaceOrderButton(sepaButtonElement);
          trackSepaExpressButtonClick(sepaButtonElement);
          clearInterval(stylingInterval);
        }
      }, INTERVAL_TIMEOUT);

      if (!errorType) {
        clearTimeout(sepaExpressTimeoutId);
      }
    });
  }, [setupPopupMiddleware, errorType]);

  const handleFinishState = useCallback(async () => {
    return new Promise((resolve, reject) => {
      window.sepaExpress.finish((message: unknown) => {
        resolve(message);
      });
      window.sepaExpress.error((error: unknown) => {
        reject(error);
      });
    });
  }, []);

  const handleSuccessResponse = useCallback((response: SepaSuccessResponseType) => {
    setErrorType(ErrorCode.NO_ERROR);
    console.log(response);
  }, []);

  const handleRejectedResponse = useCallback((response: SepaRejectResponseType) => {
    const { reasonCode } = response;
    if (reasonCode === 'SPX 704' || !reasonCode) {
      setErrorType(ErrorCode.BANKCONNECTION);
      trackPaymentEvent({ type: 'IBAN_verif_fail' });
    } else {
      setErrorType(ErrorCode.VERIFICATION);
      trackPaymentEvent({ type: 'IBAN_verif_bank_details' });
    }
  }, []);

  const setupSepaExpress = useCallback(() => {
    handleInitialState();
    handleReadyState();

    (async () => {
      try {
        const response = await handleFinishState();
        handleSuccessResponse(response as SepaSuccessResponseType);
      } catch (error) {
        handleRejectedResponse(error as SepaRejectResponseType);
      } finally {
        removeButtonContainer();
      }
    })();
  }, [
    handleFinishState,
    handleInitialState,
    handleReadyState,
    handleRejectedResponse,
    handleSuccessResponse,
  ]);

  useEffect(() => {
    const checkSepaExpress = () => {
      if (typeof window.sepaExpress !== 'undefined') {
        clearInterval(intervalId);
        setupSepaExpress();
      }
    };
    const intervalId = setInterval(checkSepaExpress, INTERVAL_TIMEOUT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {errorType && (
        <Alert variant={'error'} data-testid={`${errorType}_error`}>
          {t(`checkout.payment.sepaexpress.${errorType}.error`)}
        </Alert>
      )}
      <div
        id="sepa-express-container"
        data-testid="sepa-button-container"
        className={styles.sepaExpressContainer}></div>
      <Helmet>
        <script src={sepaExpressExternalScript} />
      </Helmet>
    </>
  );
};

export default SepaExpressButton;
